import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useUiPrefs } from "@/lib/uiPrefs";
import { DensityToggle, FilterCard, tableDensity } from "@/components/ops-ui";

export default function CostAnalysis() {
  const [rows, setRows] = useState([]);
  const [year, setYear] = useState("");
  const { density, setDensity, filtersOpen, setFiltersOpen } = useUiPrefs("cost-analysis");

  useEffect(() => {
    const url = year ? `/cost-analysis?year=${year}` : "/cost-analysis";
    api.get(url).then(r => setRows(r.data));
  }, [year]);

  const chartData = useMemo(() =>
    rows.slice(0, 15).map(r => ({ name: r.sku, cny: r.avg_cny_per_pc, rm: r.avg_rm_per_pc })),
    [rows]
  );

  return (
    <div data-testid="cost-analysis-page">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="caption">Financial</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Annual Cost Analysis</h1>
          <p className="text-slate-500 mt-1 text-sm">Landed avg cost per pc = (product cost + allocated freight) ÷ received qty.</p>
        </div>
        <DensityToggle density={density} setDensity={setDensity} />
      </header>

      <div className="mb-4">
        <FilterCard open={filtersOpen} setOpen={setFiltersOpen} activeCount={year ? 1 : 0}>
          <div className="grid sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Filter by year</label>
              <Input type="number" data-testid="year-filter-input" placeholder="e.g. 2026" value={year}
                onChange={e=>setYear(e.target.value)} className="h-9" />
            </div>
          </div>
        </FilterCard>
      </div>

      {chartData.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 mb-6">
          <div className="caption mb-2">Top SKUs · Avg CNY per pc</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="cny" fill="#0284C7" name="CNY / pc" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* desktop / tablet table (scrolls horizontally) */}
      <div className="hidden md:block rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className={`excel-table w-full ${tableDensity(density)}`} data-testid="cost-analysis-table">
          <thead><tr>
            <th>Year</th><th>SKU</th><th>Item</th><th>Rack</th><th>Qty</th>
            <th>Product CNY</th><th>Freight CNY</th><th>Total CNY</th><th>Avg CNY/pc</th>
            <th>Product RM</th><th>Freight RM</th><th>Total RM</th><th>Avg RM/pc</th>
          </tr></thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx}>
                <td className="font-mono">{r.year}</td>
                <td className="font-mono font-semibold">{r.sku}</td>
                <td>{r.name}</td>
                <td className="font-mono text-xs">{r.rack_code || "—"}</td>
                <td>{r.qty}</td>
                <td className="cny-badge">¥{r.product_cny}</td>
                <td className="cny-badge">¥{r.freight_cny}</td>
                <td className="cny-badge font-bold">¥{r.total_cny}</td>
                <td className="cny-badge font-bold">¥{r.avg_cny_per_pc}</td>
                <td className="rm-badge">RM{r.product_rm}</td>
                <td className="rm-badge">RM{r.freight_rm}</td>
                <td className="rm-badge font-bold">RM{r.total_rm}</td>
                <td className="rm-badge font-bold">RM{r.avg_rm_per_pc}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={13} className="text-center py-12 text-slate-400">No data yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* mobile cards */}
      <div className="md:hidden space-y-2">
        {rows.length === 0 && <div className="text-center py-12 text-slate-400">No data yet.</div>}
        {rows.map((r, idx) => (
          <div key={idx} data-testid={`cost-card-${idx}`} className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex items-center justify-between">
              <span className="font-mono font-semibold text-slate-800">{r.sku}</span>
              <span className="text-xs text-slate-400 font-mono">{r.year} · {r.rack_code || "—"}</span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5 truncate">{r.name}</div>
            <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <span className="text-slate-400">Qty</span><span className="text-right font-mono">{r.qty}</span>
              <span className="text-slate-400">Avg CNY/pc</span><span className="text-right cny-badge font-bold">¥{r.avg_cny_per_pc}</span>
              <span className="text-slate-400">Avg RM/pc</span><span className="text-right rm-badge font-bold">RM{r.avg_rm_per_pc}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
